/**
 * Catalogue matériel — source de vérité unique (backend + frontend).
 *
 * Extrait de `design_handoff/Inventaire matériel.dc.html` (constantes `CATALOG`,
 * `FIELDS`, `FHELP`, `SERVICE`, `SIT`). Le prototype reste la référence de fidélité.
 */

// ---------------------------------------------------------------------------
// Services responsables
// ---------------------------------------------------------------------------

/** Les 3 services qui pilotent l'inventaire. Correspondent à 3 groupes Entra. */
export const SERVICE_KEYS = ['SI', 'QPL', 'RH'] as const;
export type ServiceKey = (typeof SERVICE_KEYS)[number];

// ---------------------------------------------------------------------------
// Catégories de matériel
// ---------------------------------------------------------------------------

export const CATEGORY_KEYS = ['INFO', 'NET', 'BUREAU', 'ACCES', 'ADAPTE', 'SITE', 'ITIN'] as const;
export type CategoryKey = (typeof CATEGORY_KEYS)[number];

/** Service responsable de chaque catégorie — pilote le périmètre d'approbation des demandes. */
export const SERVICE_BY_CATEGORY: Record<CategoryKey, ServiceKey> = {
  INFO: 'SI',
  // NET (clé 4G, routeur/box, TPE) relève du QPL : monétique et matériel de terrain.
  // Tranché au lot 5 (cf. decisions.md §10). Le SI, service non restreint, agit malgré tout dessus.
  NET: 'QPL',
  BUREAU: 'QPL',
  ACCES: 'RH',
  ADAPTE: 'RH',
  SITE: 'QPL',
  ITIN: 'RH',
};

// ---------------------------------------------------------------------------
// Situation d'affectation — invisible pour le collaborateur, déduite d'une
// question simple : « où est votre matériel ? »
// ---------------------------------------------------------------------------

export const SITUATION_KEYS = ['FIXE', 'MULTI', 'HOME', 'SIEGE'] as const;
export type SituationKey = (typeof SITUATION_KEYS)[number];

/** Lieu où se trouve un matériel. `STOCKAGE` n'est jamais une situation : c'est l'état après restitution. */
export const LOCATION_KINDS = ['SITE', 'MULTI', 'HOME', 'SIEGE', 'STOCKAGE'] as const;
export type LocationKind = (typeof LOCATION_KINDS)[number];

export const SITUATIONS: Record<
  SituationKey,
  { label: string; description: string; locationKind: LocationKind }
> = {
  FIXE: {
    label: 'Site fixe',
    description: 'Mon matériel reste sur un site',
    locationKind: 'SITE',
  },
  MULTI: {
    label: 'Plusieurs sites',
    description: 'Je suis itinérant entre sites',
    locationKind: 'MULTI',
  },
  HOME: {
    label: 'Home Office',
    description: 'À mon domicile',
    locationKind: 'HOME',
  },
  SIEGE: {
    label: 'Au siège',
    description: 'Siège social — Lyon',
    locationKind: 'SIEGE',
  },
};

/** Libellé du lieu. Pour `SITE`, l'affichage réel est `code · nom` du site Bécane. */
export const LOCATION_LABELS: Record<LocationKind, string> = {
  SITE: 'Site',
  MULTI: 'Multi-site',
  HOME: 'Home Office',
  SIEGE: 'Siège',
  STOCKAGE: 'Stockage siège',
};

/** Lieu impliqué par la situation déclarée. */
export function locationKindForSituation(situation: SituationKey): LocationKind {
  return SITUATIONS[situation].locationKind;
}

export function isSituationKey(value: string): value is SituationKey {
  return (SITUATION_KEYS as readonly string[]).includes(value);
}

// ---------------------------------------------------------------------------
// État et usage — champs communs à tout matériel
// ---------------------------------------------------------------------------

export const ETATS = { OK: 'Fonctionnel', MOY: 'Abîmé / Lent', HS: 'HS' } as const;
export type Etat = keyof typeof ETATS;

export const USAGES = { SYS: 'Systématique', OCC: 'Occasionnelle', NON: 'Non utilisé' } as const;
export type Usage = keyof typeof USAGES;

// ---------------------------------------------------------------------------
// Champs de saisie
// ---------------------------------------------------------------------------

export const FIELD_KEYS = [
  'numInv',
  'marque',
  'nomPoste',
  'numSerie',
  'modele',
  'os',
  'taille',
  'operateur',
  'typeTpe',
  'numBadge',
  'totemType',
  'preciser',
  'commentaire',
] as const;
export type FieldKey = (typeof FIELD_KEYS)[number];

/** `size` rend le sélecteur visuel de taille d'écran ; `area` un commentaire multi-ligne. */
export type FieldKind = 'text' | 'area' | 'radio' | 'size';

export interface FieldDef {
  label: string;
  kind: FieldKind;
  placeholder?: string;
  /** Options des champs `radio`. */
  options?: readonly string[];
  /** Options du champ `size` : valeur stockée + repère de diagonale. */
  sizeOptions?: readonly { value: string; hint: string }[];
  /** Le champ occupe toute la largeur de la grille du formulaire. */
  wide?: boolean;
}

export const FIELDS: Record<FieldKey, FieldDef> = {
  numInv: { label: "N° d'inventaire", kind: 'text', placeholder: 'EL-00001' },
  marque: { label: 'Marque', kind: 'text', placeholder: 'Dell, Apple, HP…' },
  nomPoste: { label: 'Nom du poste', kind: 'text', placeholder: 'TDS-PC-0421' },
  numSerie: { label: 'N° de série', kind: 'text', placeholder: 'SN-XXXXXXXX' },
  modele: { label: 'Modèle', kind: 'text', placeholder: 'Référence du modèle' },
  os: { label: 'Système', kind: 'radio', options: ['iPhone (iOS)', 'Android'] },
  taille: {
    label: "Taille d'écran",
    kind: 'size',
    wide: true,
    sizeOptions: [
      { value: 'L', hint: '≈ 27"' },
      { value: 'M', hint: '≈ 24"' },
      { value: 'S', hint: '≈ 20"' },
    ],
  },
  operateur: {
    label: 'Opérateur',
    kind: 'radio',
    options: ['Orange', 'SFR', 'Bouygues', 'Free', 'Autre'],
  },
  typeTpe: { label: 'Type de connexion', kind: 'radio', options: ['Wifi', '4G', 'Ethernet'] },
  numBadge: { label: 'N° de badge', kind: 'text', placeholder: 'Ex. 0042' },
  totemType: { label: 'Orientation', kind: 'radio', options: ['Vertical', 'Horizontal'] },
  preciser: { label: 'Préciser', kind: 'text', placeholder: 'Précisez le matériel' },
  commentaire: {
    label: 'Commentaire',
    kind: 'area',
    placeholder: 'Marque, état, précisions utiles…',
    wide: true,
  },
};

/**
 * Aide « Comment trouver ? » des champs techniques. Les médias (photo/gif/vidéo) du
 * prototype sont des emplacements vides : ils le restent tant que les assets manquent.
 */
/**
 * Illustration animée d'une aide « Comment trouver ? ».
 *
 * On ne stocke que le **nom de fichier** : l'URL publique est résolue par l'application
 * (`publicAsset`), car `@caverne/shared` et `@caverne/ui` ignorent le `basePath`.
 */
export interface HelpMedia {
  /** Nom du fichier dans le dossier d'aide public (ex. `Windows11.gif`). */
  file: string;
  alt: string;
  /** Intitulé du bloc, quand l'aide se décline par système (« Windows », « Android »). */
  caption?: string;
  /** Étapes propres à ce média, lorsqu'elles diffèrent d'un système à l'autre. */
  steps?: readonly string[];
}

export interface FieldHelp {
  caption: string;
  steps: readonly string[];
  /** Illustrations, quand il en existe pour ce type de matériel. */
  media?: readonly HelpMedia[];
}

/**
 * Chemins des illustrations, relatifs au dossier d'assets publics. Regroupés ici pour que le
 * nom de fichier n'existe qu'à un seul endroit — une faute de frappe se voit alors partout à
 * la fois, plutôt que de rester silencieuse sur un seul champ.
 */
const ILLUSTRATION = {
  windows11: 'help/Windows11.gif',
  windows10: 'help/Windows10 - Tablette.gif',
  android: 'help/Android.gif',
  ios: 'help/IOS.gif',
  etiquette: 'etiquette-inventaire-TDS.png',
} as const;

export const FIELD_HELP: Partial<Record<FieldKey, FieldHelp>> = {
  numInv: {
    caption: 'Étiquette code-barres « EL-00000 »',
    steps: [
      "Au dos ou sous l'appareil, une étiquette Groupe TDS.",
      'Commence par EL- suivi de 3 à 5 chiffres.',
    ],
    // L'étiquette est la même sur tout le parc : une seule illustration, tous types confondus.
    media: [
      {
        file: ILLUSTRATION.etiquette,
        alt: "Étiquette d'inventaire Groupe TDS, avec le code-barres et le numéro EL-",
      },
    ],
  },
  numSerie: {
    caption: 'Numéro de série constructeur',
    steps: [
      'Mobile : Réglages → Informations.',
      'PC : invite de commandes → wmic bios get serialnumber.',
      "Ou sur l'étiquette sous l'appareil.",
    ],
  },
  nomPoste: {
    caption: "Nom du poste / de l'appareil",
    steps: [
      'Windows : touche Windows + Pause.',
      'Mobile : Réglages → Général → Informations → Nom.',
    ],
  },
  marque: {
    caption: 'Marque du fabricant',
    steps: ["Logo au dos ou sur la façade de l'appareil."],
  },
  modele: {
    caption: 'Référence du modèle',
    steps: ['Étiquette constructeur ou Réglages → À propos.'],
  },
};

// ---------------------------------------------------------------------------
// Types de matériel
// ---------------------------------------------------------------------------

export const TYPE_KEYS = [
  'ORDI',
  'TABLETTE',
  'TEL',
  'ECRAN',
  'CLE4G',
  'ROUTEUR',
  'TPE',
  'IMPRIMANTE',
  'FAUTEUIL',
  'BADGE_BAS',
  'BADGE_BUR',
  'FAUTEUIL_ERGO',
  'SOURIS_ERGO',
  'ADAPTE_AUTRE',
  'VITRINE',
  'FRIGO',
  'CAVE_VIN',
  'CAVE_CHOCO',
  'CIREUSE',
  'PLASTIF',
  'TOTEM',
  'TV',
  'SITE_AUTRE',
  'CHARRIOT',
  'GLACIERE',
  'PANIERE',
] as const;
export type TypeKey = (typeof TYPE_KEYS)[number];

export interface TypeDef {
  key: TypeKey;
  label: string;
  fields: readonly FieldKey[];
  /** Situations où ce type est proposé. Absent = toutes. */
  only?: readonly SituationKey[];
}

export interface CategoryDef {
  key: CategoryKey;
  label: string;
  /** Libellé d'équipe affiché à l'utilisateur — peut mentionner deux services (`NET`). */
  team: string;
  /** Situations où cette catégorie est proposée. Absent = toutes. */
  only?: readonly SituationKey[];
  note?: string;
  types: readonly TypeDef[];
}

/**
 * Beaucoup de types n'ont **aucun** champ (fauteuil, glacière, panière…) : c'est volontaire,
 * ils sont déclarables tels quels. Le prototype les jetait silencieusement (`fields.every()`
 * sur une liste vide vaut `true`) — corrigé par `isEquipmentEmpty`, voir `validation.ts`.
 */
export const CATALOG: readonly CategoryDef[] = [
  {
    key: 'INFO',
    label: 'Informatique',
    team: 'Équipe SI',
    types: [
      { key: 'ORDI', label: 'Ordinateur', fields: ['numInv', 'marque', 'nomPoste', 'numSerie'] },
      { key: 'TABLETTE', label: 'Tablette', fields: ['numInv', 'marque', 'nomPoste', 'numSerie'] },
      {
        key: 'TEL',
        label: 'Téléphone',
        fields: ['os', 'modele', 'numInv', 'marque', 'nomPoste', 'numSerie'],
      },
      { key: 'ECRAN', label: 'Écran', fields: ['taille'] },
    ],
  },
  {
    key: 'NET',
    label: 'Internet & monétique',
    team: 'Équipe QPL',
    types: [
      { key: 'CLE4G', label: 'Clé 4G', fields: ['marque', 'operateur', 'numSerie'] },
      { key: 'ROUTEUR', label: 'Routeur / Box', fields: ['marque', 'operateur', 'numSerie'] },
      { key: 'TPE', label: 'TPE (paiement)', fields: ['typeTpe', 'modele', 'numSerie'] },
    ],
  },
  {
    key: 'BUREAU',
    label: 'Bureau',
    team: 'Équipe QPL',
    types: [
      { key: 'IMPRIMANTE', label: 'Imprimante', fields: ['marque', 'modele', 'numInv'] },
      { key: 'FAUTEUIL', label: 'Fauteuil', fields: [], only: ['HOME'] },
    ],
  },
  {
    key: 'ACCES',
    label: 'Accès',
    team: 'Équipe RH',
    only: ['SIEGE'],
    types: [
      { key: 'BADGE_BAS', label: 'Badge du bas', fields: ['numBadge'] },
      { key: 'BADGE_BUR', label: 'Badge bureau', fields: ['numBadge'] },
    ],
  },
  {
    key: 'ADAPTE',
    label: 'Matériel adapté',
    team: 'Équipe RH',
    note: 'Équipement pour les porteurs de handicap. Une simple déclaration suffit.',
    types: [
      { key: 'FAUTEUIL_ERGO', label: 'Fauteuil ergonomique', fields: [] },
      { key: 'SOURIS_ERGO', label: 'Souris ergonomique', fields: [] },
      { key: 'ADAPTE_AUTRE', label: 'Autre', fields: ['preciser'] },
    ],
  },
  {
    key: 'SITE',
    label: 'Équipement de site',
    team: 'Équipe QPL',
    only: ['FIXE'],
    note: 'Équipement présent sur le site. Un commentaire est demandé pour chacun.',
    types: [
      { key: 'VITRINE', label: 'Vitrine réfrigérée', fields: ['commentaire'] },
      { key: 'FRIGO', label: 'Frigo', fields: ['commentaire'] },
      { key: 'CAVE_VIN', label: 'Cave à vin', fields: ['commentaire'] },
      { key: 'CAVE_CHOCO', label: 'Cave à chocolat', fields: ['commentaire'] },
      { key: 'CIREUSE', label: 'Cireuse', fields: ['commentaire'] },
      { key: 'PLASTIF', label: 'Plastifieuse', fields: ['commentaire'] },
      { key: 'TOTEM', label: 'Totem', fields: ['totemType', 'commentaire'] },
      { key: 'TV', label: 'TV', fields: ['commentaire'] },
      { key: 'SITE_AUTRE', label: 'Autre', fields: ['commentaire'] },
    ],
  },
  {
    key: 'ITIN',
    label: 'Équipement itinérant',
    team: 'Équipe RH',
    only: ['MULTI'],
    types: [
      { key: 'CHARRIOT', label: 'Charriot', fields: [] },
      { key: 'GLACIERE', label: 'Glacière', fields: [] },
      { key: 'PANIERE', label: 'Panière', fields: [] },
    ],
  },
];

// ---------------------------------------------------------------------------
// Index dérivés — construits une fois au chargement du module
// ---------------------------------------------------------------------------

export const CATEGORY_BY_KEY = Object.fromEntries(
  CATALOG.map((category) => [category.key, category]),
) as Record<CategoryKey, CategoryDef>;

export const TYPE_BY_KEY = Object.fromEntries(
  CATALOG.flatMap((category) => category.types.map((type) => [type.key, type])),
) as Record<TypeKey, TypeDef>;

export const CATEGORY_OF_TYPE = Object.fromEntries(
  CATALOG.flatMap((category) => category.types.map((type) => [type.key, category.key])),
) as Record<TypeKey, CategoryKey>;

export const CATEGORY_LABELS = Object.fromEntries(
  CATALOG.map((category) => [category.key, category.label]),
) as Record<CategoryKey, string>;

// ---------------------------------------------------------------------------
// Aide « Comment trouver ? » déclinée par type de matériel
//
// `FIELD_HELP` indexe l'aide par **champ**. Or trouver le nom d'un poste ne se fait pas de
// la même manière sur un PC, une tablette ou un téléphone — alors que les trois portent le
// même champ `nomPoste`. Cette table surcharge donc l'aide **par type**, et `fieldHelp()`
// retombe sur `FIELD_HELP` pour tout ce qui n'y figure pas.
// ---------------------------------------------------------------------------

/** Les illustrations, sans leurs étapes : celles-ci dépendent du champ à renseigner. */
const WINDOWS_11: HelpMedia = {
  file: ILLUSTRATION.windows11,
  alt: 'Windows 11 — la recherche « Système » ouvre la page d’informations du poste',
  caption: 'Windows',
};
const WINDOWS_10: HelpMedia = {
  file: ILLUSTRATION.windows10,
  alt: 'Windows 10 — la recherche « Système » ouvre la page d’informations de la tablette',
  caption: 'Windows',
};
const ANDROID: HelpMedia = {
  file: ILLUSTRATION.android,
  alt: 'Android — page « À propos du téléphone » dans les réglages',
  caption: 'Android',
};
const IPHONE: HelpMedia = {
  file: ILLUSTRATION.ios,
  alt: 'iPhone — page « Informations » dans les réglages',
  caption: 'iPhone',
};

/** Reprend une illustration en lui attachant les étapes propres au champ. */
function illustrated(media: HelpMedia, steps: readonly string[]): HelpMedia {
  return { ...media, steps };
}

// Les chemins d'accès ne varient pas d'un champ à l'autre : seule change la dernière étape,
// celle qui dit quelle valeur relever. On les compose donc plutôt que de les recopier.
const REGLAGES = 'Rends-toi sur l’application « Réglages ».';

const windowsSteps = (champ: string): readonly string[] => [
  'Tape « Système » dans la barre de recherche Windows.',
  `Copie la valeur du champ « ${champ} ».`,
];

const androidSteps = (...suite: string[]): readonly string[] => [
  REGLAGES,
  'Descends dans la rubrique « Système », puis clique sur « À propos du téléphone ».',
  ...suite,
];

const iphoneSteps = (...suite: string[]): readonly string[] => [
  REGLAGES,
  'Va dans la rubrique « Général », puis clique sur « Informations ».',
  ...suite,
];

const releve = (champ: string) => `Copie la valeur du champ « ${champ} ».`;

/** Légendes reprises de l'aide générique du champ, pour n'avoir qu'une seule formulation. */
const CAPTION = {
  nomPoste: FIELD_HELP.nomPoste!.caption,
  numSerie: FIELD_HELP.numSerie!.caption,
  modele: FIELD_HELP.modele!.caption,
} as const;

/**
 * Aide propre à un couple (type, champ). Chaque combinaison illustrée est explicite : le
 * chemin d'accès et la valeur à relever diffèrent d'un champ à l'autre, même quand l'écran
 * de destination est le même.
 */
export const FIELD_HELP_BY_TYPE: Partial<Record<TypeKey, Partial<Record<FieldKey, FieldHelp>>>> = {
  ORDI: {
    nomPoste: {
      caption: CAPTION.nomPoste,
      steps: [],
      media: [illustrated(WINDOWS_11, windowsSteps('Nom de l’appareil'))],
    },
    numSerie: {
      caption: CAPTION.numSerie,
      steps: [],
      media: [illustrated(WINDOWS_11, windowsSteps('Numéro de série'))],
    },
  },
  TABLETTE: {
    nomPoste: {
      caption: CAPTION.nomPoste,
      steps: [],
      media: [illustrated(WINDOWS_10, windowsSteps('Nom de l’appareil'))],
    },
    numSerie: {
      caption: CAPTION.numSerie,
      steps: [],
      media: [illustrated(WINDOWS_10, windowsSteps('Numéro de série'))],
    },
  },
  TEL: {
    modele: {
      caption: CAPTION.modele,
      steps: [],
      media: [
        illustrated(ANDROID, androidSteps(releve('Modèle'))),
        illustrated(IPHONE, iphoneSteps(releve('Nom du modèle'))),
      ],
    },
    nomPoste: {
      caption: CAPTION.nomPoste,
      steps: [],
      media: [
        illustrated(ANDROID, androidSteps(releve('Nom de l’appareil'))),
        illustrated(IPHONE, iphoneSteps(releve('Nom'))),
      ],
    },
    numSerie: {
      caption: CAPTION.numSerie,
      steps: [],
      media: [
        illustrated(
          ANDROID,
          androidSteps('Descends dans la rubrique « État ».', releve('Numéro de série')),
        ),
        illustrated(IPHONE, iphoneSteps(releve('Numéro de série'))),
      ],
    },
  },
};

/**
 * Aide à afficher pour un champ, dans le contexte d'un type de matériel : l'aide propre au
 * couple (type, champ) si elle existe, sinon l'aide générique du champ, sinon `undefined`
 * (le champ n'a pas d'aide).
 *
 * Chaque combinaison illustrée est écrite explicitement plutôt que déduite : une illustration
 * ne s'affiche jamais sans le texte qui dit quelle valeur y relever.
 */
export function fieldHelp(typeKey: TypeKey, fieldKey: FieldKey): FieldHelp | undefined {
  return FIELD_HELP_BY_TYPE[typeKey]?.[fieldKey] ?? FIELD_HELP[fieldKey];
}

export function typeLabel(key: TypeKey): string {
  return TYPE_BY_KEY[key]?.label ?? key;
}

export function isTypeKey(value: string): value is TypeKey {
  return value in TYPE_BY_KEY;
}

/** Catégories proposées pour une situation (`ACCES` au siège, `SITE` sur site fixe…). */
export function applicableCategories(situation: SituationKey): CategoryDef[] {
  return CATALOG.filter((category) => !category.only || category.only.includes(situation));
}

/** Types proposés dans une catégorie pour une situation (le fauteuil n'existe qu'en Home Office). */
export function applicableTypes(category: CategoryDef, situation: SituationKey): TypeDef[] {
  return category.types.filter((type) => !type.only || type.only.includes(situation));
}

/** Un type est-il déclarable dans cette situation ? Revérifié côté serveur, jamais seulement dans l'UI. */
export function isTypeAllowed(typeKey: TypeKey, situation: SituationKey): boolean {
  const category = CATEGORY_BY_KEY[CATEGORY_OF_TYPE[typeKey]];
  if (category.only && !category.only.includes(situation)) return false;
  const type = TYPE_BY_KEY[typeKey];
  return !type.only || type.only.includes(situation);
}

// ---------------------------------------------------------------------------
// Rattachement du matériel
// ---------------------------------------------------------------------------

/** Le matériel est rattaché soit à une personne, soit à un site (« Matériel du site »). */
export const OWNER_KINDS = ['USER', 'SITE'] as const;
export type OwnerKind = (typeof OWNER_KINDS)[number];

/**
 * Seule la catégorie `SITE` est rattachée au site plutôt qu'au déclarant — et elle n'existe
 * qu'en situation `FIXE`, donc un site est toujours connu. L'itinérant (`ITIN`, situation
 * `MULTI`) n'a par construction aucun site : il reste rattaché à la personne.
 */
export function ownerKindForType(typeKey: TypeKey): OwnerKind {
  return CATEGORY_OF_TYPE[typeKey] === 'SITE' ? 'SITE' : 'USER';
}

// ---------------------------------------------------------------------------
// Intégration FleetDM — seuls ces types ont un host Fleet
// ---------------------------------------------------------------------------

export const FLEET_TYPE_KEYS = ['ORDI', 'TABLETTE'] as const;

export function hasFleetHost(typeKey: string): boolean {
  return (FLEET_TYPE_KEYS as readonly string[]).includes(typeKey);
}

/** Adresse de restitution du matériel (affichée dans la modale de rebut / restitution). */
export const STOCKAGE_SIEGE_ADRESSE = '21 Rue Commandant Fuzier, 69003 Lyon';
